import { z } from 'zod';
import { API_KEY_SCOPES, API_KEY_STATUSES, MEMBER_STATUSES, MEMBERSHIP_STATUSES, PAYMENT_STATUSES, SESSION_STATUSES, WEBHOOK_DELIVERY_ERRORS, WEBHOOK_DELIVERY_EVENTS, WEBHOOK_DELIVERY_STATUSES, WEBHOOK_EVENTS } from '@bulk/types';
import { dateTimeSchema, idSchema, paginationQuerySchema, patchOf } from './common';

export const API_KEY_NAME_MAX = 80;
export const WEBHOOK_URL_MAX = 2048;
export const WEBHOOK_DESCRIPTION_MAX = 200;
export const API_KEY_ADDRESSES_MAX = 20;

export const apiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export const webhookEventSchema = z.enum(WEBHOOK_EVENTS);
export const webhookDeliveryEventSchema = z.enum(WEBHOOK_DELIVERY_EVENTS);

const uniqueList = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .min(1)
    .refine((items) => new Set(items).size === items.length, { message: 'List each value once' });

export const createApiKeySchema = z.object({
  name: z.string().trim().min(1).max(API_KEY_NAME_MAX).describe('What the key is for, e.g. "Accountant export"'),
  scopes: uniqueList(apiKeyScopeSchema).describe('What the key may read; a key never writes'),
  expiresAt: dateTimeSchema.nullable().default(null).describe('When the key stops working; null for never'),
  allowedAddresses: z
    .array(z.string().trim().min(1).max(64))
    .max(API_KEY_ADDRESSES_MAX)
    .default([])
    .describe('Addresses or networks (CIDR, e.g. 203.0.113.0/24) the key may be used from; empty for anywhere. 422 INVALID_NETWORK for one that does not parse'),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

export const apiKeySchema = z.object({
  id: idSchema,
  name: z.string(),
  prefix: z.string().describe('The start of the key, enough to tell keys apart; the rest is never shown again'),
  scopes: z.array(apiKeyScopeSchema),
  allowedAddresses: z.array(z.string()).describe('Where the key may be used from; empty for anywhere'),
  status: z.enum(API_KEY_STATUSES),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable().describe('Updated at most every few minutes'),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiKeyDto = z.infer<typeof apiKeySchema>;

export const createdApiKeySchema = z.object({
  apiKey: apiKeySchema,
  secret: z.string().describe('The whole key. Shown this once: Bulk keeps only a digest of it'),
});
export type CreatedApiKeyDto = z.infer<typeof createdApiKeySchema>;

const webhookEndpointFields = z.object({
  url: z.string().trim().min(1).max(WEBHOOK_URL_MAX).describe('An https address on the public internet'),
  description: z.string().trim().max(WEBHOOK_DESCRIPTION_MAX).nullable().default(null),
  events: uniqueList(webhookEventSchema),
});

export const createWebhookEndpointSchema = webhookEndpointFields;
export type CreateWebhookEndpointInput = z.infer<typeof createWebhookEndpointSchema>;

export const updateWebhookEndpointSchema = patchOf(webhookEndpointFields).extend({
  enabled: z.boolean().optional().describe('Switching an endpoint back on clears its failures'),
});
export type UpdateWebhookEndpointInput = z.infer<typeof updateWebhookEndpointSchema>;

export const webhookEndpointSchema = z.object({
  id: idSchema,
  url: z.string(),
  description: z.string().nullable(),
  events: z.array(webhookEventSchema),
  enabled: z.boolean(),
  autoDisabledAt: z.string().nullable().describe('Set when Bulk switched the endpoint off because it kept failing'),
  consecutiveFailures: z.number().int(),
  failingSince: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  previousSecretValidUntil: z.string().nullable().describe('Until when the replaced signing secret still signs beside the current one; null when only the current one does'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WebhookEndpointDto = z.infer<typeof webhookEndpointSchema>;

export const webhookEndpointWithSecretSchema = z.object({
  endpoint: webhookEndpointSchema,
  signingSecret: z.string().describe('Signs every delivery. Shown this once, when made or replaced'),
});
export type WebhookEndpointWithSecretDto = z.infer<typeof webhookEndpointWithSecretSchema>;

export const rotateWebhookSecretSchema = z.object({
  overlap: z.boolean().default(true).describe('Keep the replaced secret signing beside the new one for 24 hours; false ends it at once, as for a secret that leaked'),
});
export type RotateWebhookSecretInput = z.infer<typeof rotateWebhookSecretSchema>;

export const webhookDeliveryListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
});
export type WebhookDeliveryListQuery = z.infer<typeof webhookDeliveryListQuerySchema>;

export const webhookDeliverySchema = z.object({
  id: idSchema,
  endpointId: idSchema,
  eventId: idSchema,
  event: webhookDeliveryEventSchema,
  status: z.enum(WEBHOOK_DELIVERY_STATUSES),
  attempts: z.number().int(),
  responseStatus: z.number().int().nullable(),
  responseBody: z.string().nullable().describe('The start of what the receiver answered'),
  error: z.enum(WEBHOOK_DELIVERY_ERRORS).nullable().describe('Why the last attempt got no answer; an answer is recorded in responseStatus instead'),
  durationMs: z.number().int().nullable(),
  nextAttemptAt: z.string().nullable(),
  lastAttemptAt: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type WebhookDeliveryDto = z.infer<typeof webhookDeliverySchema>;

export const externalMemberSchema = z.object({
  id: idSchema,
  memberNumber: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  joinedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExternalMemberDto = z.infer<typeof externalMemberSchema>;

export const externalMembershipSchema = z.object({
  id: idSchema,
  memberId: idSchema,
  planId: idSchema,
  planName: z.string(),
  status: z.enum(MEMBERSHIP_STATUSES),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
});
export type ExternalMembershipDto = z.infer<typeof externalMembershipSchema>;

export const externalPaymentSchema = z.object({
  id: idSchema,
  memberId: idSchema,
  status: z.enum(PAYMENT_STATUSES),
  purpose: z.string(),
  method: z.string(),
  provider: z.string(),
  amountMinor: z.number().int(),
  refundedMinor: z.number().int(),
  currency: z.string(),
  description: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  locationId: idSchema.nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExternalPaymentDto = z.infer<typeof externalPaymentSchema>;

export const externalVisitSchema = z.object({
  id: idSchema,
  memberId: idSchema,
  locationId: idSchema.nullable(),
  readerId: idSchema.nullable(),
  source: z.string().describe('How the entry was recorded: manual at the desk, or the door system that read the card'),
  occurredAt: z.string(),
});
export type ExternalVisitDto = z.infer<typeof externalVisitSchema>;

export const externalClassSchema = z.object({
  id: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  defaultDurationMinutes: z.number().int(),
  defaultCapacity: z.number().int(),
  active: z.boolean(),
});
export type ExternalClassDto = z.infer<typeof externalClassSchema>;

export const externalClassSessionSchema = z.object({
  id: idSchema,
  classId: idSchema,
  className: z.string(),
  locationId: idSchema,
  trainerId: idSchema.nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  capacity: z.number().int(),
  booked: z.number().int(),
  status: z.enum(SESSION_STATUSES),
});
export type ExternalClassSessionDto = z.infer<typeof externalClassSessionSchema>;

export const externalUpdatedSinceQuerySchema = paginationQuerySchema.extend({
  updatedSince: dateTimeSchema.optional().describe('Only rows changed at or after this instant, oldest change first'),
});
export type ExternalUpdatedSinceQuery = z.infer<typeof externalUpdatedSinceQuerySchema>;

export const externalRangeQuerySchema = paginationQuerySchema.extend({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
});
export type ExternalRangeQuery = z.infer<typeof externalRangeQuerySchema>;

export const webhookEventEnvelopeSchema = z.object({
  id: idSchema.describe('The event; a resend carries the same id, so a receiver can ignore one it has seen'),
  type: webhookDeliveryEventSchema,
  createdAt: z.string(),
  data: z.record(z.string(), z.unknown()),
});
export type WebhookEventEnvelope = z.infer<typeof webhookEventEnvelopeSchema>;
